// imports
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';
import apiClient from 'API/client';
import { API_ENDPOINTS } from 'API/endpoints';

// project imports
import NavGroup from './NavGroup';
// import menuItem from 'menu-items';
import { isGlobalReadAllRole } from 'utils/roles';
import { fetchGetDevice } from 'API/device/getConfig';

import dashboard from 'menu-items/dashboard';
import adminMenu from 'menu-items/admin';
import Pages from 'menu-items/pages';

// ==============================|| SIDEBAR MENU LIST ||============================== //
const DEVICE_LAST_ACCESS_KEY_PREFIX = 'diiot:last-device-access';

const buildLastAccessStorageKey = (userId) => `${DEVICE_LAST_ACCESS_KEY_PREFIX}:${userId ?? 'anonymous'}`;

const readLastAccessMap = (userId) => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(buildLastAccessStorageKey(userId));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeLastAccessMap = (userId, map) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(buildLastAccessStorageKey(userId), JSON.stringify(map));
  } catch (error) {
    // no-op
  }
};

const DEVICE_CODE_PATH_PATTERNS = [
  /^\/pages\/[^/]+\/(\d+)(?:\/|$)/i,
  /^\/datapoint\/modificarDatapoint\/(\d+)(?:\/|$)/i,
  /^\/dispositivo\/modificarDispositivo\/(\d+)(?:\/|$)/i
];

const extractDeviceCodeFromPath = (pathname = '') => {
  for (const pattern of DEVICE_CODE_PATH_PATTERNS) {
    const match = pathname.match(pattern);
    if (match) return Number(match[1]);
  }
  return null;
};

const MenuList = () => {
  const location = useLocation();
  const { userData, loading } = useSelector((state) => state.user);
  const [devices, setDevices] = useState([]);
  const [expandedDevice, setExpandedDevice] = useState(null);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [lastAccessMap, setLastAccessMap] = useState({});
  const [deviceStatusByCode, setDeviceStatusByCode] = useState({});

  useEffect(() => {
    if (loading || !userData?.UserId) return;
    setLastAccessMap(readLastAccessMap(userData.UserId));
  }, [loading, userData]);

  useEffect(() => {
    if (loading || !userData?.UserId) return;
    const deviceCode = extractDeviceCodeFromPath(location.pathname);
    if (!deviceCode) return;

    const nextMap = {
      ...readLastAccessMap(userData.UserId),
      [deviceCode]: Date.now()
    };

    writeLastAccessMap(userData.UserId, nextMap);
    setLastAccessMap(nextMap);
  }, [loading, userData, location.pathname]);

  useEffect(() => {
    const routeDeviceCode = extractDeviceCodeFromPath(location.pathname);
    if (!routeDeviceCode) return;
    setExpandedDevice(routeDeviceCode);
  }, [location.pathname]);

  useEffect(() => {
    const loadDevices = async () => {
      if (loading || !userData) return;

      let statusByCode = {};
      try {
        const statusResponse = await apiClient.get(API_ENDPOINTS.DEVICE.GET_STATUS);
        const statusList = Array.isArray(statusResponse.data) ? statusResponse.data : [];
        statusByCode = statusList.reduce((acc, statusItem) => {
          const code = Number(statusItem?.DeviceCode);
          if (Number.isFinite(code)) {
            acc[code] = {
              connectionStatus: statusItem?.ConnectionStatus,
              lastUpdate: statusItem?.LastUpdate || null
            };
          }
          return acc;
        }, {});
      } catch (error) {
        console.error('No se pudo cargar estado de conexión de dispositivos:', error);
      }
      setDeviceStatusByCode(statusByCode);

      const entities = userData.Entities || [];
      const isGlobalAdmin = isGlobalReadAllRole(userData);

      if (isGlobalAdmin) {
        const allMappedFromEntities = entities.flatMap((entity) => entity.Localities?.flatMap((locality) => locality.Devices ?? []) ?? []);

        // Traer configuración global para incluir dispositivos huérfanos (sin entidad/locación)
        let allDevicesFromConfig = [];
        try {
          const configDevices = await fetchGetDevice(null);
          const list = Array.isArray(configDevices) ? configDevices : [];
          allDevicesFromConfig = list.map((device) => ({
            DeviceCode: device.DeviceCode,
            DeviceName: device.DeviceName,
            DeviceActive: device.DeviceActive
          }));
        } catch (error) {
          // fallback silencioso: usar lo disponible en userData
          console.error('No se pudo cargar lista global de dispositivos:', error);
        }

        const mergedDevices = [...allMappedFromEntities, ...allDevicesFromConfig]
          .filter(
            (device, index, self) => index === self.findIndex((candidate) => Number(candidate.DeviceCode) === Number(device.DeviceCode))
          )
          .sort((a, b) => Number(a.DeviceCode) - Number(b.DeviceCode));

        setDevices(mergedDevices);
        return;
      }

      // Determinar la entidad a usar para el filtrado en usuarios no admin
      let targetEntity = null;
      if (userData.DefaultEntityId !== null) {
        targetEntity = entities.find((entity) => entity.EntityId === userData.DefaultEntityId);
      } else if (entities.length > 0) {
        targetEntity = entities.reduce((prev, current) => (prev.EntityId > current.EntityId ? prev : current));
      }

      if (targetEntity) {
        const extractedDevices =
          targetEntity.Localities?.flatMap((locality) => {
            return locality.Devices?.filter((device) => Number(device.DeviceActive) === 1) ?? [];
          }) || [];

        const dedupedByCode = extractedDevices.filter(
          (device, index, self) => index === self.findIndex((candidate) => Number(candidate.DeviceCode) === Number(device.DeviceCode))
        );

        setDevices(dedupedByCode);
      } else {
        setDevices([]);
      }
    };

    loadDevices();
  }, [loading, userData]);

  // Expandir menu dispositivo
  const handleExpand = (deviceCode) => {
    setExpandedDevice(deviceCode);
  };

  const devicesSortedByRecentUse = useMemo(() => {
    const enrichedDevices = devices.map((device, originalIndex) => ({
      ...device,
      __originalIndex: originalIndex,
      __lastAccessAt: Number(lastAccessMap[Number(device.DeviceCode)] || 0)
    }));

    enrichedDevices.sort((a, b) => {
      if (b.__lastAccessAt !== a.__lastAccessAt) {
        return b.__lastAccessAt - a.__lastAccessAt;
      }
      return a.__originalIndex - b.__originalIndex;
    });

    return enrichedDevices.map(({ __originalIndex, __lastAccessAt, ...device }) => device);
  }, [devices, lastAccessMap]);

  const filteredDevices = useMemo(() => {
    if (!deviceSearch.trim()) {
      return devicesSortedByRecentUse;
    }

    const query = deviceSearch.trim().toLowerCase();

    return devicesSortedByRecentUse.filter((device) => {
      const deviceName = (device.DeviceName ?? device.Name ?? device.DeviceAlias ?? '').toString().toLowerCase();
      const deviceCode = (device.code_id ?? device.CodeId ?? device.CodeID ?? device.DeviceCode ?? '').toString().toLowerCase();

      return deviceName.includes(query) || deviceCode.includes(query);
    });
  }, [devicesSortedByRecentUse, deviceSearch]);

  // menu-items.index.js
  const menuItem = {
    items: [dashboard]
  };

  const isStrictSystemAdmin = String(userData?.Role || '').trim() === 'System admin';
  if (isStrictSystemAdmin) {
    menuItem.items.push(adminMenu);
  }

  if (filteredDevices.length > 0) {
    const datapointsItem = Pages(filteredDevices, expandedDevice, handleExpand, deviceStatusByCode);
    datapointsItem.search = {
      value: deviceSearch,
      onChange: (value) => setDeviceSearch(value)
    };
    menuItem.items.push(datapointsItem);
  }

  const navItems = menuItem.items.map((item) => {
    switch (item.type) {
      case 'group':
        return <NavGroup key={item.id} item={item} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  return <>{navItems}</>;
};

export default MenuList;
